import React, { useEffect, useState, useRef, useCallback } from "react";
import { FaFileExcel, FaFilePdf, FaSave } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
 import { Helmet } from "react-helmet";

import {
  getReportSettingsApi,
  updateReportSettingsApi,
  downloadReportApi,
  generateReportNowApi,
} from "../../apis/reports.api";
import { useDarkMode } from "../../hooks/useDarkMode";
import { toast } from "react-toastify";

export default function ReportsData() {
  const { isDarkMode } = useDarkMode();
  const navigate = useNavigate();
  const hasFetchedData = useRef(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [authError, setAuthError] = useState(false);

  // Form state
  const [reportFrequency, setReportFrequency] = useState("Daily");
  const [reportFormat, setReportFormat] = useState("PDF");
  const [autoMailReports, setAutoMailReports] = useState(false);

  // Track original values
  const [originalValues, setOriginalValues] = useState({
    reportFrequency: "Daily",
    reportFormat: "PDF",
    autoMailReports: false,
  });

  const fetchReportSettings = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getReportSettingsApi();
      const data = response.data;

      setReportFrequency(data.report_frequency || "Daily");
      setReportFormat(data.report_format || "PDF");
      setAutoMailReports(data.auto_mail_reports || false);

      setOriginalValues({
        reportFrequency: data.report_frequency || "Daily",
        reportFormat: data.report_format || "PDF",
        autoMailReports: data.auto_mail_reports || false,
      });

      setIsDirty(false);
    } catch (error) {
      console.error("Error fetching report settings:", error);

      // Handle authentication errors
      if (error.response?.status === 401 || error.response?.status === 403) {
        setAuthError(true);
        toast.error("Session expired. Please sign in again.", {
          toastId: "session-expired",
        });
        setTimeout(() => {
          localStorage.clear();
          navigate("/signin");
        }, 1500);
      } else {
        toast.error("Failed to load report settings", {
          toastId: "load-error",
        });
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    // Prevent duplicate calls in StrictMode
    if (hasFetchedData.current) return;
    hasFetchedData.current = true;

    // Check if user is authenticated before making API calls
    const token = localStorage.getItem("token");
    if (!token) {
      setAuthError(true);
      setLoading(false);
      toast.error("Please sign in to access report settings", {
        toastId: "auth-error",
      });
      setTimeout(() => navigate("/signin"), 1500);
      return;
    }
    fetchReportSettings();
  }, [navigate, fetchReportSettings]);

  useEffect(() => {
    const hasChanged =
      reportFrequency !== originalValues.reportFrequency ||
      reportFormat !== originalValues.reportFormat ||
      autoMailReports !== originalValues.autoMailReports;
    setIsDirty(hasChanged);
  }, [reportFrequency, reportFormat, autoMailReports, originalValues]);

  const handleDownloadReport = async () => {
    try {
      toast.info("Generating report...", { toastId: "generating" });
      await generateReportNowApi();
      const response = await downloadReportApi();
      const blob =
        response.data instanceof Blob
          ? response.data
          : new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const extension = reportFormat === "Excel" ? "xlsx" : "pdf";
      link.setAttribute(
        "download",
        `seo-report-${new Date().toISOString().split("T")[0]}.${extension}`,
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.dismiss("generating");
      toast.success("Report downloaded successfully!", {
        toastId: "download-success",
      });
    } catch (error) {
      console.error("Error downloading report:", error);
      toast.dismiss("generating");

      let errorMessage = "Failed to download report";

      // Handle authentication errors
      if (error.response?.status === 401 || error.response?.status === 403) {
        errorMessage = "Session expired. Please sign in again.";
        toast.error(errorMessage, { toastId: "session-expired-download" });
        setTimeout(() => {
          localStorage.clear();
          navigate("/signin");
        }, 1500);
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
        toast.error(errorMessage, { toastId: "download-error" });
      } else if (error.message) {
        errorMessage = error.message;
        toast.error(errorMessage, { toastId: "download-error" });
      } else {
        toast.error(errorMessage, { toastId: "download-error" });
      }
    }
  };

  if (loading) {
    return (
      <div
        className={`min-h-[calc(100vh-120px)] ${isDarkMode ? "bg-gray-950" : "bg-gray-50"} p-6 flex items-center justify-center`}
      >
        <div className="flex flex-col items-center gap-4">
          <div
            className={`w-12 h-12 border-4 rounded-full animate-spin ${isDarkMode ? "border-gray-700 border-t-green-500" : "border-gray-300 border-t-[#008300]"}`}
          ></div>
          <p
            className={`text-lg font-medium ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
          >
            {authError
              ? "Redirecting to sign in..."
              : "Loading report settings..."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
   
<Helmet>

  {/* Title */}
  <title>SEO Reports | Seo Up Rank Dashboard</title>

  {/* Description */}
  <meta
    name="description"
    content="View detailed SEO reports, analytics insights, keyword rankings and website optimization data in your Seo Up Rank dashboard."
  />

  {/* Private page */}
  <meta name="robots" content="noindex, nofollow" />

  {/* Canonical */}
  <link rel="canonical" href="https://novarsis.ai/reports" />

  {/* Open Graph */}
  <meta property="og:title" content="Seo Up Rank SEO Reports Dashboard" />
  <meta
    property="og:description"
    content="Analyze your website SEO performance with advanced reports and analytics from Seo Up Rank."
  />
  <meta property="og:type" content="website" />
  <meta property="og:url" content="https://novarsis.ai/reports" />
  <meta property="og:site_name" content="Seo Up Rank" />

  {/* Twitter */}
  <meta name="twitter:card" content="summary" />
  <meta name="twitter:title" content="Seo Up Rank Reports" />
  <meta
    name="twitter:description"
    content="Access SEO reports and analytics insights from your Seo Up Rank dashboard."
  />

  {/* Schema */}
  <script type="application/ld+json">
    {`
    {
      "@context":"https://schema.org",
      "@type":"WebPage",
      "name":"SEO Reports - Seo Up Rank",
      "url":"https://novarsis.ai/reports",
      "description":"Dashboard page displaying SEO reports and analytics insights for website optimization."
    }
    `}
  </script>

</Helmet>
    <div
      className={`min-h-[calc(100vh-120px)] px-4 sm:px-6 lg:px-8 py-8 sm:py-10 ${isDarkMode ? "bg-gray-950" : "bg-gray-50"}`}
    >
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-10"
        >
          <h1
            className={`text-4xl sm:text-5xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}
          >
            Report Settings
          </h1>
          <p
            className={`mt-3 text-base sm:text-lg ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
          >
            Configure your SEO report preferences and download reports on
            demand.
          </p>
        </motion.div>

        {/* Main Content Cards */}
        <div className="space-y-8">
          {/* Report Format Section */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0 }}
            className={`rounded-xl p-6 sm:p-8 border-2 ${isDarkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"}`}
          >
            <div className="mb-8">
              <div className="flex items-center gap-4 mb-2">
                <div
                  className={`w-12 h-12 rounded-lg flex items-center justify-center ${isDarkMode ? "bg-blue-900/50 text-blue-400" : "bg-blue-100 text-blue-600"}`}
                >
                  <FaFilePdf size={24} />
                </div>
                <div>
                  <h2
                    className={`text-2xl sm:text-3xl font-bold ${isDarkMode ? "text-gray-100" : "text-gray-900"}`}
                  >
                    Report Format
                  </h2>
                  <p
                    className={`text-sm sm:text-base mt-1 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                  >
                    Choose your preferred export format
                  </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                {
                  format: "PDF",
                  icon: <FaFilePdf className="text-3xl" />,
                  title: "PDF Format",
                  desc: "Professional & portable",
                  color: "red",
                },
                {
                  format: "Excel",
                  icon: <FaFileExcel className="text-3xl" />,
                  title: "Excel Format",
                  desc: "Data analysis & spreadsheet",
                  color: "green",
                },
              ].map((item) => (
                <motion.button
                  key={item.format}
                  whileHover={{ y: -4 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setReportFormat(item.format)}
                  className={`p-6 rounded-xl border-2 transition-all duration-300 group ${
                    reportFormat === item.format
                      ? isDarkMode
                        ? `border-${item.color}-500 bg-${item.color}-900/30 shadow-lg shadow-${item.color}-500/20`
                        : `border-${item.color}-500 bg-${item.color}-50 shadow-lg shadow-${item.color}-500/20`
                      : isDarkMode
                        ? "border-gray-700 hover:border-gray-600 bg-gray-800/50"
                        : "border-gray-300 hover:border-gray-400 bg-gray-50/50"
                  }`}
                >
                  <div
                    className={`flex flex-col items-center gap-3 ${reportFormat === item.format ? (isDarkMode ? "text-white" : "text-gray-900") : isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                  >
                    <div
                      className={`transition-transform duration-300 group-hover:scale-110 ${reportFormat === item.format ? `text-${item.color}-400` : isDarkMode ? `text-${item.color}-400` : `text-${item.color}-600`}`}
                    >
                      {item.icon}
                    </div>
                    <div>
                      <p className="font-bold text-base sm:text-lg">
                        {item.title}
                      </p>
                      <p className="text-xs sm:text-sm mt-1 opacity-75">
                        {item.desc}
                      </p>
                    </div>
                  </div>
                </motion.button>
              ))}
            </div>
          </motion.div>

          {/* Download Report Section */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.1 }}
            className={`rounded-xl p-6 sm:p-8 border-2 ${isDarkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"}`}
          >
            <div className="mb-8">
              <div className="flex items-center gap-4 mb-2">
                <div
                  className={`w-12 h-12 rounded-lg flex items-center justify-center ${isDarkMode ? "bg-purple-900/50 text-purple-400" : "bg-purple-100 text-purple-600"}`}
                >
                  <FaSave size={24} />
                </div>
                <div>
                  <h2
                    className={`text-2xl sm:text-3xl font-bold ${isDarkMode ? "text-gray-100" : "text-gray-900"}`}
                  >
                    Download Report
                  </h2>
                  <p
                    className={`text-sm sm:text-base mt-1 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                  >
                    Generate and download your latest SEO report
                  </p>
                </div>
              </div>
            </div>

            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleDownloadReport}
              disabled={saving}
              className={`w-full py-4 sm:py-5 rounded-lg font-bold text-lg transition-all duration-200 shadow-lg hover:shadow-xl ${
                saving
                  ? isDarkMode
                    ? "bg-gray-700 text-gray-400 cursor-not-allowed"
                    : "bg-gray-300 text-gray-500 cursor-not-allowed"
                  : isDarkMode
                    ? "bg-gradient-to-r from-[#008300] to-green-600 hover:from-[#006400] hover:to-[#008300] text-white"
                    : "bg-gradient-to-r from-[#008300] to-green-600 hover:from-[#006400] hover:to-[#008300] text-white"
              }`}
            >
              {saving ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
                  Generating Report...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <FaSave size={18} />
                  Download {reportFormat} Report
                </span>
              )}
            </motion.button>

            <p
              className={`text-sm text-center mt-4 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
            >
              📊 Reports are generated on-demand in {reportFormat} format
            </p>
          </motion.div>

          {/* Info Section */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.2 }}
            className={`rounded-xl p-6 sm:p-8 border-2 border-l-4 ${isDarkMode ? "bg-gray-800 border-gray-700 border-l-green-500" : "bg-blue-50 border-blue-200 border-l-blue-500"}`}
          >
            <h3
              className={`text-lg font-bold mb-4 flex items-center gap-2 ${isDarkMode ? "text-gray-100" : "text-gray-900"}`}
            >
              <span className="text-xl">ℹ️</span>
              Report Information
            </h3>
            <ul
              className={`space-y-3 text-sm sm:text-base ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
            >
              <li className="flex gap-3">
                <span className="text-[#008300] font-bold">✓</span>
                <span>
                  Reports are generated in your selected format automatically
                </span>
              </li>
              <li className="flex gap-3">
                <span className="text-[#008300] font-bold">✓</span>
                <span>Download your latest report directly from this page</span>
              </li>
              <li className="flex gap-3">
                <span className="text-[#008300] font-bold">✓</span>
                <span>
                  Your report includes comprehensive SEO metrics and insights
                </span>
              </li>
            </ul>
          </motion.div>
        </div>
      </div>
    </div></>
  );
}
